import { useEffect, useRef } from "react"

interface UseBrackyEventsProps {
  onTradeActivity?: () => void
  onScroll?: () => void
  playWink3D?: () => void
  playSideEye?: () => void
  playWince?: (pressed: boolean) => void
}

export function useBrackyEvents({
  onTradeActivity,
  onScroll,
  playWink3D,
  playSideEye,
  playWince,
}: UseBrackyEventsProps) {
  const firstBurstSeenRef = useRef(false)
  const burstTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const nextIsSideRef = useRef(false)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const handleTradeActivity = (e: Event) => {
      const ev = e as CustomEvent<{ active: boolean }>
      if (!ev.detail?.active) return

      if (!firstBurstSeenRef.current) {
        firstBurstSeenRef.current = true
        return
      }

      if (burstTimeoutRef.current) {
        clearTimeout(burstTimeoutRef.current)
      }

      burstTimeoutRef.current = setTimeout(() => {
        if (onTradeActivity) onTradeActivity()
        if (nextIsSideRef.current && playSideEye) {
          playSideEye()
        } else if (playWink3D) {
          playWink3D()
        }
        nextIsSideRef.current = !nextIsSideRef.current
        burstTimeoutRef.current = null
      }, 500)
    }

    window.addEventListener("tradeActivity", handleTradeActivity)
    return () => {
      window.removeEventListener("tradeActivity", handleTradeActivity)
      if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current)
    }
  }, [onTradeActivity, playWink3D, playSideEye])

  useEffect(() => {
    if (!playWince) return

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      if (onScroll) onScroll()
      playWince(true)

      scrollTimeoutRef.current = setTimeout(() => {
        playWince(false)
      }, 150) as unknown as NodeJS.Timeout
    }

    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [onScroll, playWince])
}
